<script setup>
import { ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Api from 'dashboard/api/customers';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CustomerForm from './CustomerForm.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const form = ref(null);
const query = ref('');
const type = ref('');
const status = ref('');
const page = ref(1);
const customers = ref([]);
const total = ref(0);
const perPage = ref(15);
const error = ref('');
const { run, isPending } = useAbortableRequest();
const load = async () => {
  error.value = '';
  try {
    const response = await run(signal =>
      Api.get(
        {
          q: query.value,
          customer_type: type.value,
          referral_status: status.value,
          page: page.value,
        },
        signal
      )
    );
    if (!response) return;
    customers.value = response.data.payload;
    total.value = response.data.meta.count;
    perPage.value = response.data.meta.per_page;
  } catch (e) {
    error.value = e.response?.data?.error || t('CUSTOMERS.ERROR');
  }
};
watch([query, type, status, () => route.params.accountId], () => {
  page.value = 1;
  load();
});
watch(page, load, { immediate: true });
const show = customer =>
  router.push({
    name: 'customers_show',
    params: { accountId: route.params.accountId, customerId: customer.id },
  });
</script>

<template>
  <section class="flex h-full w-full flex-col bg-n-background text-n-slate-12">
    <header
      class="flex items-center justify-between border-b border-n-weak px-8 py-6"
    >
      <div>
        <h1 class="text-heading-1">{{ t('CUSTOMERS.TITLE') }}</h1>
        <p class="mt-1 text-n-slate-11">{{ t('CUSTOMERS.SUBTITLE') }}</p>
      </div>
      <Button
        :label="t('CUSTOMERS.ADD')"
        icon="i-lucide-plus"
        @click="form.open()"
      />
    </header>
    <div class="flex flex-wrap items-end gap-4 px-8 py-5">
      <Input
        v-model="query"
        :label="t('CUSTOMERS.SEARCH')"
        class="min-w-64 flex-1"
      />
      <label class="flex flex-col gap-2 text-sm"
        >{{ t('CUSTOMERS.TYPE') }}
        <select
          v-model="type"
          class="rounded-lg border border-n-weak bg-n-surface-1 p-2.5"
        >
          <option value="">{{ t('CUSTOMERS.ALL') }}</option>
          <option value="lead">{{ t('CUSTOMERS.TYPES.lead') }}</option>
          <option value="existing_client">
            {{ t('CUSTOMERS.TYPES.existing_client') }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-2 text-sm"
        >{{ t('CUSTOMERS.STATUS') }}
        <select
          v-model="status"
          class="rounded-lg border border-n-weak bg-n-surface-1 p-2.5"
        >
          <option value="">{{ t('CUSTOMERS.ALL') }}</option>
          <option
            v-for="item in ['none', 'pending', 'qualified']"
            :key="item"
            :value="item"
          >
            {{ t(`CUSTOMERS.STATUSES.${item}`) }}
          </option>
        </select>
      </label>
    </div>
    <p v-if="error" role="alert" class="px-8 py-4 text-n-ruby-11">
      {{ error }}
    </p>
    <div class="flex-1 overflow-auto px-8" :aria-busy="isPending">
      <table class="w-full text-start text-sm">
        <thead class="border-b border-n-weak text-n-slate-11">
          <tr>
            <th
              v-for="column in [
                'NAME',
                'TYPE',
                'EXPIRY',
                'REFERRED_BY',
                'REWARDS',
                'STATUS',
                'ACTIONS',
              ]"
              :key="column"
              class="px-3 py-4 text-start font-medium"
            >
              {{ t(`CUSTOMERS.${column}`) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="customer in customers"
            :key="customer.id"
            class="border-b border-n-weak hover:bg-n-surface-1"
          >
            <td class="px-3 py-5">
              <button
                class="font-medium text-n-blue-11"
                @click="show(customer)"
              >
                {{ customer.name }}
              </button>
              <p class="mt-1 text-n-slate-11">
                {{ customer.email || customer.phone_number }}
              </p>
            </td>
            <td class="px-3 py-5">
              {{ t(`CUSTOMERS.TYPES.${customer.customer_type}`) }}
            </td>
            <td class="px-3 py-5">
              {{ customer.expires_on || t('CUSTOMERS.NOT_SET') }}
            </td>
            <td class="px-3 py-5">
              <RouterLink
                v-if="customer.referral"
                :to="{
                  name: 'customers_show',
                  params: {
                    accountId: route.params.accountId,
                    customerId: customer.referral.referrer_id,
                  },
                }"
                class="text-n-blue-11"
                >{{ customer.referral.referrer_name }}</RouterLink
              ><span v-else>{{ t('CUSTOMERS.NOT_SET') }}</span>
            </td>
            <td class="px-3 py-5">
              {{ t('CUSTOMERS.MONTHS', { count: customer.reward_months }) }}
            </td>
            <td class="px-3 py-5">
              {{
                t(`CUSTOMERS.STATUSES.${customer.referral?.status || 'none'}`)
              }}
            </td>
            <td class="px-3 py-5">
              <Button
                variant="ghost"
                :label="t('CUSTOMERS.VIEW')"
                @click="show(customer)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <p
        v-if="!customers.length && !isPending"
        class="py-16 text-center text-n-slate-11"
      >
        {{ t('CUSTOMERS.EMPTY') }}
      </p>
    </div>
    <PaginationFooter
      v-if="total"
      v-model:current-page="page"
      :total-items="total"
      :items-per-page="perPage"
    />
    <CustomerForm ref="form" @saved="show" />
  </section>
</template>
